import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { currentUserQuery, useCurrentUser } from '@/features/auth/use-current-user';
import { useOpenPortal, useStartCheckout } from '@/features/billing/use-billing';
import { ApiRequestError } from '@/lib/api';

interface BillingSearch {
  checkout?: 'success' | 'cancelled';
}

export const Route = createFileRoute('/app/billing')({
  validateSearch: (search: Record<string, unknown>): BillingSearch =>
    search.checkout === 'success' || search.checkout === 'cancelled'
      ? { checkout: search.checkout }
      : {},
  component: BillingPage,
});

function BillingPage() {
  const { checkout } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation(['billing', 'common', 'errors']);
  const { data: user } = useCurrentUser();
  const startCheckout = useStartCheckout();
  const openPortal = useOpenPortal();

  // After returning from Checkout the webhook may lag by a second or two: poll briefly.
  useEffect(() => {
    if (checkout !== 'success') return;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      const fresh = await queryClient.fetchQuery(currentUserQuery);
      if (fresh?.billing.subscribed || tries >= 10) {
        clearInterval(timer);
        void navigate({ to: '/app/billing', search: {}, replace: true });
      }
    }, 1500);
    return () => clearInterval(timer);
  }, [checkout, navigate, queryClient]);

  if (!user) return null;
  const { billing } = user;
  const fmtDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(iso)) : '';
  const error = startCheckout.error ?? openPortal.error;
  const errorCode = error instanceof ApiRequestError ? error.code : error ? 'generic' : null;

  const comped = Boolean(billing.compedUntil && new Date(billing.compedUntil) > new Date());

  const statusLine = (() => {
    // A comp outranks the free line, which would otherwise invite somebody to
    // subscribe for what they already have.
    if (comped) return t('billing:compedLine', { date: fmtDate(billing.compedUntil) });
    if (!billing.subscribed) return t('billing:freeLine');
    switch (billing.status) {
      case 'active':
      case 'trialing':
        return billing.cancelAtPeriodEnd
          ? t('billing:endsOn', { date: fmtDate(billing.currentPeriodEndsAt) })
          : t('billing:renews', { date: fmtDate(billing.currentPeriodEndsAt) });
      default:
        return t(`billing:status.${billing.status}`);
    }
  })();

  const canSubscribe = !billing.subscribed && !comped;
  const canManage = billing.subscribed;

  return (
    <div className="max-w-xl space-y-6">
      <h1 className="text-xl font-semibold">{t('billing:title')}</h1>

      {checkout === 'success' ? (
        <p className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {t('billing:checkoutSuccess')}
        </p>
      ) : null}
      {checkout === 'cancelled' ? (
        <p className="rounded border border-neutral-200 bg-neutral-100 px-3 py-2 text-sm">
          {t('billing:checkoutCancelled')}
        </p>
      ) : null}

      <section className="rounded-lg border border-neutral-200 bg-white p-5">
        <div className="text-sm text-neutral-500">{t('billing:plan.label')}</div>
        <div className="mt-1 text-lg font-medium">
          {billing.paid ? t('billing:plan.paidName') : t('billing:plan.freeName')}
        </div>
        <p className="mt-1 text-sm text-neutral-600">
          {billing.paid ? t('billing:plan.paidDescription') : t('billing:plan.freeDescription')}
        </p>
        <div className="mt-4 text-sm">
          <span
            className={`mr-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
              billing.paid || !billing.subscribed
                ? 'bg-green-100 text-green-800'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {/* `status` is still "none" for a comp: nothing was bought. */}
            {comped ? t('billing:compedBadge') : t(`billing:status.${billing.status}`)}
          </span>
          {statusLine}
        </div>

        {!billing.configured ? (
          <p className="mt-4 text-xs text-neutral-500">{t('billing:notConfigured')}</p>
        ) : (
          <div className="mt-5 flex gap-3">
            {canSubscribe ? (
              <button
                type="button"
                onClick={() => startCheckout.mutate()}
                disabled={startCheckout.isPending}
                className="inline-flex min-h-11 items-center justify-center rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40 sm:min-h-0"
              >
                {t('billing:subscribe')}
              </button>
            ) : null}
            {canManage ? (
              <button
                type="button"
                onClick={() => openPortal.mutate()}
                disabled={openPortal.isPending}
                className="inline-flex min-h-11 items-center justify-center rounded border border-neutral-300 bg-white px-4 py-2 text-sm font-medium disabled:opacity-40 sm:min-h-0"
              >
                {t('billing:manage')}
              </button>
            ) : null}
          </div>
        )}
        {errorCode ? <p className="mt-3 text-sm text-red-600">{t(`errors:${errorCode}`)}</p> : null}
      </section>
    </div>
  );
}
